
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Plus, Tag, X, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useContentTags } from "@/hooks/useContentTags";
import AddTagButton from "@/components/common/AddTagButton";
import { useHiddenTags } from "@/hooks/useHiddenTags";
import { cn } from "@/lib/utils";
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem } from "@/components/ui/context-menu";

interface ContentTagsProps {
  contentId: string;
}

const ContentTags = ({ contentId }: ContentTagsProps) => {
  const { contentTags, removeTag, isLoading } = useContentTags(contentId);
  const { isTagHidden, toggleTagVisibility } = useHiddenTags();

  const handleRemoveTag = (tagId: string) => {
    removeTag(tagId);
  };

  const handleToggleTagVisibility = (tagId: string) => {
    toggleTagVisibility(tagId);
  };
  
  const handleEditTag = (tag: any) => {
    // Remove edit functionality - just log for now
    console.log("Edit tag functionality removed:", tag.name);
  };

  return (
    <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-md">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-1 uppercase text-sm font-medium text-gray-700 dark:text-gray-300">
          <Tag size={16} />
          <span>TAGS</span>
        </div>
        
        <AddTagButton 
          variant="circle" 
          contentId={contentId}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {isLoading ? (
          <div className="w-full text-center py-2 text-sm text-gray-500">
            Loading tags...
          </div>
        ) : contentTags.length > 0 ? (
          contentTags.map((tag) => (
            <ContextMenu key={tag.id}>
              <ContextMenuTrigger>
                <Badge 
                  variant="outline" 
                  className={cn("bg-white dark:bg-gray-700 flex items-center gap-1 px-3 py-1", {
                    "opacity-50": isTagHidden(tag.id)
                  })}
                  style={{ borderColor: tag.color, color: tag.color }}
                >
                  <span className={cn({
                    "line-through": isTagHidden(tag.id)
                  })}>
                    {tag.name}
                  </span>
                  <button 
                    className="ml-1 hover:text-red-500 transition-colors" 
                    aria-label="Remove tag"
                    onClick={() => handleRemoveTag(tag.id)}
                  >
                    <X size={14} />
                  </button>
                </Badge>
              </ContextMenuTrigger>
              <ContextMenuContent className="w-[220px]">
                <ContextMenuItem onClick={() => handleToggleTagVisibility(tag.id)}>
                  {isTagHidden(tag.id) ? (
                    <>
                      <Eye className="mr-2 h-4 w-4" />
                      Show Tagged Content
                    </>
                  ) : (
                    <>
                      <EyeOff className="mr-2 h-4 w-4" />
                      Hide Tagged Content
                    </>
                  )}
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          ))
        ) : (
          <div className="w-full text-center py-2 text-sm text-gray-500">
            No tags added yet
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentTags;
